import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import theme from "theme";

import Alert from "components/Alert";
import Autocomplete from "components/Autocomplete";
import Box from "components/Box";
import Button from "components/Button";
import Card from "components/Card";
import Dialog from "components/Dialog";
import Loading, { CircularProgress } from "components/Loading";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";

import {
  superuserGuessedConnectionUpdate,
  superuserResendGuessedConnectionsForUpdate,
  useSuperuserChecklistFileImport,
  useSuperuserCompanyCustomFields,
  useSuperuserGuessedConnections,
} from "entities/Superuser/sdk";

import { ConnectionResendDialog } from "./ConnectionResendDialog";
import { extractCustomFieldPaths, extractIdToPathMapping } from "./utils";

export interface IDetails {
  checklistFileImportId: number;
  handleGuessedConnectionsSubmit: () => void;
  dealId: number;
  userId: number;
  showLoading: boolean;
  enableGuessedConnectionFetching: boolean;
  guessedConnectionFetchingSetter: ({
    enableFetching,
  }: {
    enableFetching: boolean;
  }) => void;
}

const Details = ({
  checklistFileImportId,
  dealId,
  userId,
  handleGuessedConnectionsSubmit,
  showLoading,
  enableGuessedConnectionFetching,
  guessedConnectionFetchingSetter,
}: IDetails) => {
  const [shouldShowResendDialog, setShouldShowResendDialog] =
    useState<boolean>(true);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

  const {
    data: checklistFileImport,
    mutate: mutateChecklistFileImport,
    isValidating: isFetichingChecklistFileImport,
  } = useSuperuserChecklistFileImport({
    checklistFileImportId,
  });

  const {
    data: guessedConnections,
    isValidating: isFetchingGuessedConnections,
    mutate: mutateGuessedConnections,
  } = useSuperuserGuessedConnections({
    checklistFileImportId: checklistFileImportId,
    enabled: enableGuessedConnectionFetching,
  });

  const { data: customFields, isValidating: isFetchingCustomFields } =
    useSuperuserCompanyCustomFields({
      dealId,
      userId,
      enabled:
        !isFetchingGuessedConnections ||
        !_.isEmpty(guessedConnections) ||
        !isFetichingChecklistFileImport,
    });

  const successfulMatchesCount = useMemo(
    () =>
      guessedConnections?.filter((connection) => !_.isNil(connection.field_id))
        .length,
    [guessedConnections]
  );

  const options = useMemo(
    () =>
      !isFetchingCustomFields && !_.isNil(customFields)
        ? extractCustomFieldPaths({ data: customFields })
        : [],
    [customFields, isFetchingCustomFields]
  );

  const fieldIdToFullPathMapping = useMemo(
    () => extractIdToPathMapping({ options }),
    [options]
  );

  const handleOnChange = useCallback(
    ({
      connectionId,
      fieldId,
    }: {
      connectionId: number;
      fieldId: number | null;
    }) => {
      superuserGuessedConnectionUpdate({
        connectionId,
        fieldId,
      })
        .then(() => {
          mutateGuessedConnections();
          setShouldShowResendDialog(false);
        })
        .catch((errors) => {
          const errorMessage = errors?.message || errors?.detail;
          if (!_.isNil(errorMessage)) {
            toast.errorMessage(errorMessage);
          } else {
            errors?.forEach?.((error: unknown) => {
              if (_.isString(error)) {
                toast.errorMessage(error);
              }
            });
          }
        });
    },
    [mutateGuessedConnections]
  );

  const onConfirmResendConnectionsDialogButton = useCallback(() => {
    superuserResendGuessedConnectionsForUpdate({
      checklistFileImportId: checklistFileImportId,
    }).then(() => {
      mutateChecklistFileImport();
      setShouldShowResendDialog(false);
      guessedConnectionFetchingSetter({ enableFetching: false });
    });
  }, [
    setShouldShowResendDialog,
    checklistFileImportId,
    mutateChecklistFileImport,
    guessedConnectionFetchingSetter,
  ]);

  const onCloseResendConnectionsDialogButton = useCallback(() => {
    setShouldShowResendDialog(false);
  }, []);

  const isResendConnectionsDialogOpen = useMemo(
    () =>
      shouldShowResendDialog &&
      !_.isNil(successfulMatchesCount) &&
      !_.isNil(guessedConnections) &&
      successfulMatchesCount < Math.ceil(guessedConnections?.length / 2),
    [shouldShowResendDialog, successfulMatchesCount, guessedConnections]
  );

  return _.isNil(checklistFileImport) || showLoading ? (
    <Loading open />
  ) : (
    <Stack spacing={1} marginTop={1}>
      <Card
        elevation={2}
        sx={{
          padding: theme.spacing(0, 2, 2, 2),
          top: 0,
        }}
      >
        <Stack>
          <Text variant="h6">Checklist File Import Details</Text>
          <Text variant="body1">
            Status: <b>{checklistFileImport.status.toLowerCase()}</b>
          </Text>
          <Text variant="body1">
            Deal: <b>{checklistFileImport.deal.name}</b>
          </Text>
          <Text variant="body1">
            Value creator:{" "}
            <b>
              {checklistFileImport.value_creator.first_name}{" "}
              {checklistFileImport.value_creator.last_name}(
              {checklistFileImport.value_creator.email})
            </b>
          </Text>
        </Stack>
      </Card>

      {(!enableGuessedConnectionFetching || isFetchingCustomFields) && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={6}
          padding={8}
          flexGrow={1}
          height="calc(100vh - 390px)"
        >
          <CircularProgress />
          <Text variant="h2" textAlign="center">
            Please wait while the file to field mappings are being created.
          </Text>
        </Stack>
      )}

      {enableGuessedConnectionFetching && !isFetchingCustomFields && (
        <Stack direction="column" justifyContent="space-between">
          {guessedConnections?.map((connection) => (
            <Stack
              key={`${connection.id} ${connection.file.original_file_name} ${connection.field_id}`}
              display="grid"
              gridTemplateColumns="2fr 10fr"
              alignItems="center"
              gap={1}
              spacing={2}
            >
              <Tooltip
                title={connection.file.original_file_name}
                placement="top-start"
              >
                <Text textOverflow="ellipsis" overflow="hidden" noWrap>
                  {connection.file.original_file_name}
                </Text>
              </Tooltip>
              <Tooltip
                placement="top-start"
                title={
                  !_.isNil(connection.field_id)
                    ? fieldIdToFullPathMapping[connection.field_id]
                    : ""
                }
              >
                <Autocomplete
                  defaultValue={options.find((option) =>
                    !_.isNil(connection.field_id)
                      ? option.value === connection.field_id.toString()
                      : ""
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        _.isNil(connection.field_id)
                          ? "No file will be uploaded"
                          : ""
                      }
                    />
                  )}
                  renderOption={(params, option) => (
                    <li {...params} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  options={options}
                  onChange={(e, option) => {
                    handleOnChange({
                      connectionId: connection.id,
                      fieldId: !_.isNil(option) ? Number(option.value) : null,
                    });
                  }}
                />
              </Tooltip>
            </Stack>
          ))}
          {!_.isNil(guessedConnections) && (
            <Stack
              margin={1}
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              gap={2}
            >
              <Alert severity="warning">
                You are about to import {successfulMatchesCount} out of the{" "}
                {guessedConnections.length} files you uploaded.
              </Alert>
              <Button
                onClick={() => setIsConfirmationOpen(true)}
                disabled={
                  guessedConnections.filter((connection) => connection.field_id)
                    .length === 0
                }
              >
                Submit
              </Button>
            </Stack>
          )}
          <Dialog
            open={isConfirmationOpen}
            maxWidth="sm"
            fullWidth
            onClose={() => setIsConfirmationOpen(false)}
          >
            <Box alignSelf="center" padding={4}>
              <Text variant="text1">
                Are you sure you want to proceed with the checklist file import?
              </Text>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="text"
                  onClick={() => setIsConfirmationOpen(false)}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button onClick={handleGuessedConnectionsSubmit}>
                  Confirm
                </Button>
              </Stack>
            </Box>
          </Dialog>
          <ConnectionResendDialog
            open={isResendConnectionsDialogOpen}
            onClose={onCloseResendConnectionsDialogButton}
            onConfirm={onConfirmResendConnectionsDialogButton}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default Details;
